import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getProductDetails } from "../services";
import { ProductResponse } from "../types/product.type";
import { featureAccess } from "../permissions/access";
import { useUserRole } from "../utils/hooks/useUserRole";
import { useProductCategories } from "../components/Product/Products/useProductCategories";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ProductDetails from "../components/Product/Products/Details";
import ProductDrawer from "../components/Product/Products/ProductDrawer";
import { PRODUCT_LIST_PATH } from "../components/Product/shared/constants";

export default function ProductDetailsPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const role = useUserRole();
  const canEditCatalogue = featureAccess.productWrite(role);
  const { categories, categoryNameByCode, reloadCategories } =
    useProductCategories();

  const [productDetails, setProductDetails] = useState<ProductResponse>();
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

  const getDetails = useCallback(async (uuid: string) => {
    const {
      data: { data },
    } = await getProductDetails(uuid);

    setProductDetails(data);
  }, []);

  useEffect(() => {
    if (productId) getDetails(productId);
  }, [productId, getDetails]);

  const handleOpenEdit = () => {
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  const handleSaved = () => {
    if (productId) getDetails(productId);
  };

  // This page describes one product, so once it is deleted there is nothing to read.
  const handleDeleted = () => navigate(PRODUCT_LIST_PATH);

  if (!productDetails) return null;

  const categoryName = productDetails.category_code
    ? categoryNameByCode.get(productDetails.category_code) ??
      productDetails.category_code
    : "";

  return (
    <>
      <Box sx={{ padding: "8px 0" }}>
        <Stack direction="row" alignItems="center" gap={1} mb={2}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            สินค้า
          </Typography>
          <Typography variant="body1">/</Typography>
          <Link
            to={PRODUCT_LIST_PATH}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              รายการสินค้า
            </Typography>
          </Link>
          <Typography variant="body1">/</Typography>
          <Typography variant="body1" color="primary">
            {productDetails.name}
          </Typography>
        </Stack>

        <ProductDetails
          productDetails={productDetails}
          categoryName={categoryName}
          canEdit={canEditCatalogue}
          onEdit={handleOpenEdit}
        />
      </Box>
      <ProductDrawer
        open={drawerOpen}
        mode="edit"
        productUuid={productId}
        categories={categories}
        onClose={handleCloseDrawer}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
        onReloadCategories={reloadCategories}
      />
    </>
  );
}
